// ReceiptImage.tsx
import { CSSProperties, useEffect, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { downloadReceiptImage } from '../../../../../services/api';

const HEIC_RE = /\.(heic|heif)$/i;

interface ReceiptImageProps {
    url: string;
    fileName?: string;
    alt?: string;
    style?: CSSProperties;
    className?: string;
    receiptId?: number;
}

export const ReceiptImage = ({ url, fileName, alt, style, className, receiptId }: ReceiptImageProps) => {
    const isHeic = !!fileName && HEIC_RE.test(fileName);
    const [src, setSrc] = useState<string | undefined>(isHeic ? undefined : url);
    const [converting, setConverting] = useState(isHeic);
    const [failed, setFailed] = useState(false);
    const [imgError, setImgError] = useState(false);

    useEffect(() => {
        let objectUrl: string | undefined;
        let cancelled = false;

        setImgError(false);
        setFailed(false);

        if (!isHeic) {
            setSrc(url);
            setConverting(false);
            return;
        }

        setConverting(true);

        (async () => {
            try {
                const res = await fetch(url);
                const blob = await res.blob();
                const heic2any = (await import('heic2any')).default;
                const converted = (await heic2any({
                    blob,
                    toType: 'image/jpeg',
                    quality: 0.8
                })) as Blob;
                if (cancelled) return;
                objectUrl = URL.createObjectURL(converted);
                setSrc(objectUrl);
            } catch (e) {
                if (!cancelled) setFailed(true);
            } finally {
                if (!cancelled) setConverting(false);
            }
        })();

        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [url, isHeic]);

    if (converting) {
        return <Spinner variant="primary" size="sm" />;
    }
    if (failed || imgError || !src) {
        if (receiptId) {
            return (
                <button
                    type="button"
                    className="btn btn-link p-0 text-info fw-bolder"
                    style={{ fontSize: '.875rem', textDecoration: 'underline' }}
                    onClick={() => downloadReceiptImage(receiptId, fileName ?? 'receipt-image')}
                >
                    Download image
                </button>
            );
        }
        return null;
    }
    return (
        <img
            src={src}
            alt={alt ?? 'Receipt'}
            className={className}
            style={{ display: 'block', maxWidth: '100%', maxHeight: 600, objectFit: 'contain', ...style }}
            onError={() => setImgError(true)}
        />
    );
};